import React, {useEffect, useState} from 'react'
import {useNavigate, useLocation, useBlocker} from "react-router";
import { inject, observer } from "mobx-react";
import { toast } from "react-toastify";

import SpinnerAnimation from '../../components/Spinner'
import FormLayout from "./FormLayout";
import {getMessageObject, MessageType} from "./FormLayout/MessageFactory";

const Verification = ({
  userStore,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {state: {username = "", password = "", message = null} = {}} = location;
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  const [code, setCode] = useState("");

  // WEBAPP-3N: Warn user if they try to navigate away before completing verification.
  // Only block when the form is not yet submitted (verification not complete).
  const blocker = useBlocker(
    ({currentLocation, nextLocation}) =>
      !formSubmitted && currentLocation.pathname !== nextLocation.pathname
  );

  // WEBAPP-3N: Also warn on browser tab close / page refresh before verification is done.
  useEffect(() => {
    if (formSubmitted) return;
    const handleBeforeUnload = e => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formSubmitted]);

  // Convert info messages to toast, keep error/warning messages in form
  const [messageObject, setMessageObject] = useState(() => {
    if (message && message.type === "info") {
      toast.info(message.message);
      return null;
    }
    return message;
  });

  const handleSubmitClick = e => {
    setFormSubmitted(true);
    setMessageObject(null);
    console.log("Verification: username=", username, "password exists=", !!password, "code=", code);
    userStore.confirmRegistration(username, code)
      .then(() => {
        console.log("Confirmation successful");
        // Only attempt automatic login if we have the password
        if (password) {
          console.log("Attempting automatic login");
          return userStore.login(username, password, navigate)
            .then(() => {
              console.log("Login successful");
              navigate("/");
            });
        } else {
          // No password available, redirect to sign-in page
          console.log("No password available, redirecting to sign-in");
          navigate("/signin", {
            replace: true,
            state: {
              username,
              message: getMessageObject(MessageType.UserConfirmed, username)
            }
          });
        }
      })
      .catch(error => {
        console.error("Confirmation/login error", error, "password was=", !!password);
        setFormSubmitted(false);
        // After successful confirmation, a NotAuthorizedException means the
        // PreAuthentication Lambda rejected the user (not an invalid password).
        if (error.name === "NotAuthorizedException") {
          navigate("/signin", {
            replace: true,
            state: {
              username,
              message: getMessageObject(MessageType.NotAuthorized)
            }
          });
        } else {
          setMessageObject({message: error?.message || "Confirmation failed", type: "error", name: error.name});
        }
      });
  };

  // PLEX-1414: Add resend confirmation functionality
  const handleResendClick = e => {
    setResendingCode(true);
    setMessageObject(null);
    userStore
      .resendConfirmation(username)
      .then(result => {
        setResendingCode(false);
        // Use toast for informational message
        const email = username || "your email";
        toast.info(`A new confirmation code has been sent to ${email}. Enter it here to complete registration.`);
      })
      .catch(error => {
        setResendingCode(false);
        if (error.name === "LimitExceededException") {
          setMessageObject({message: "Too many retries, please try again later", type: "error"});
        } else if (error.name === "InvalidParameterException") {
          // User is already confirmed
          setMessageObject(getMessageObject(MessageType.AlreadyConfirmed, username));
        } else {
          setMessageObject({message: error?.message || "Failed to resend confirmation code", type: "error", name: error.name});
        }
      });
  };

  return (
    <FormLayout message={messageObject}>
      {blocker.state === 'blocked' && (
        <div className="nav-blocker-overlay">
          <p>You haven't verified your email yet. Are you sure you want to leave?</p>
          <button
            type="button"
            className="login-submit"
            onClick={() => blocker.proceed()}
          >
            Leave anyway
          </button>
          {' '}
          <button
            type="button"
            className="signup-btn"
            onClick={() => blocker.reset()}
          >
            Stay and verify
          </button>
        </div>
      )}
      <div>
        <p>
          <input
            type         = "text"
            className    = { "login-control-code" }
            autoComplete = "code"
            placeholder  = "Enter your confirmation code"
            value        = { code }
            onChange     = { e => setCode(e.target.value) }
            disabled     = { formSubmitted ? "disabled" : "" }
          />
        </p>
        <p>
          <button autoFocus
                  className = "login-submit"
                  onClick   = { handleSubmitClick }
                  disabled  = { formSubmitted || !code ? "disabled" : "" }
          >
            {formSubmitted ? "Verifying..." : "Verify"}
            <SpinnerAnimation style={{ float: "right", display: formSubmitted ? "" : "none" }}/>
          </button>
        </p>
        <p className="signup-block">
          <button
            type = "button"
            className = "signup-btn"
            onClick = { handleResendClick }
            disabled = { resendingCode || formSubmitted ? "disabled" : "" }
          >
            {resendingCode ? "Sending..." : "Resend confirmation code"}
            <SpinnerAnimation style={{ float: "right", display: resendingCode ? "" : "none" }}/>
          </button>
        </p>
      </div>
    </FormLayout>
  )
}

export default inject("userStore")(observer(Verification));
